import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { renderDocument } from '@atlaskit/renderer/render-document';
import TextSerializer from '@atlaskit/renderer/text';

import type { DetailsSummaryADFQuery_detailsLines_detailLines } from '../queries/__types__/DetailsSummaryADFQuery.ts';
import type { SortOrders } from '../pprTypes.ts';

import { getColumnComparisons } from './getColumnComparisons.ts';
import { getADFParser } from './getADFParser.ts';

const isNumeric = (num: string | number) =>
	(typeof num === 'number' || (typeof num === 'string' && num.trim() !== '')) &&
	!isNaN(num as number);

const compareStringsForSort = (stringA: string, stringB: string, sortOrder: SortOrders) => {
	if (sortOrder === 'ascending') {
		return stringA.localeCompare(stringB, undefined, {
			numeric: isNumeric(stringA) && isNumeric(stringB),
		});
	} else {
		return stringB.localeCompare(stringA, undefined, {
			numeric: isNumeric(stringA) && isNumeric(stringB),
		});
	}
};

type SortRows = {
	rows: (DetailsSummaryADFQuery_detailsLines_detailLines | null)[];
	sortOrder: SortOrders;
	sortHeader: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	adfHeaderIndex?: number;
	contentId: string;
};

export const sortRows = ({
	adfHeaderIndex,
	contentId,
	createAnalyticsEvent,
	rows,
	sortHeader,
	sortOrder,
}: SortRows): (DetailsSummaryADFQuery_detailsLines_detailLines | null)[] => {
	if (sortOrder === undefined) return rows;
	const adfParser = getADFParser({ contentId, createAnalyticsEvent });
	// Cache the parsed ADF to avoid redundant parsing and doc rendering on subsequent sort comparisons
	const cache = new Map<string, string>();

	return rows.slice().sort((a, b) => {
		if (!a || !b) return 0;

		// adfHeaderIndex would only be defined when an adf column header is clicked
		if (adfHeaderIndex !== undefined) {
			// turn ADF into plain text in order to compare the strings more accurately
			const textSerializer = new TextSerializer(defaultSchema);

			const textA =
				cache.get(a.details[adfHeaderIndex]!) ||
				(renderDocument(adfParser(a.details[adfHeaderIndex]), textSerializer).result ?? '');
			const textB =
				cache.get(b.details[adfHeaderIndex]!) ||
				(renderDocument(adfParser(b.details[adfHeaderIndex]), textSerializer).result ?? '');

			cache.set(a.details[adfHeaderIndex]!, textA);
			cache.set(b.details[adfHeaderIndex]!, textB);

			return compareStringsForSort(textA, textB, sortOrder);
		}

		const [aSort, bSort] = getColumnComparisons(a, b, sortHeader);

		if (typeof aSort === 'string' && typeof bSort === 'string') {
			return compareStringsForSort(aSort, bSort, sortOrder);
		} else if (typeof aSort === 'number' && typeof bSort === 'number') {
			return sortOrder === 'ascending' ? aSort - bSort : bSort - aSort;
		} else return 0;
	});
};
